<template>
  <div class="cy-plugin-widget-iframe">
    <CyEmptyData
      v-if="hasError"
      :title="$t('plugins.widgetUnavailableTitle')"
      :text="$t('plugins.widgetUnavailableText')">
      <template
        v-if="errorDetail"
        #actions>
        <p
          class="cy-plugin-widget-iframe__error-detail"
          role="alert"
          v-html="$sanitizeHtml(errorDetail)"/>
      </template>
    </CyEmptyData>
    <!-- Cross-origin widget content keeps the sandbox plus the origin/popup escapes it needs;
         same-origin content gets no sandbox, since allow-scripts + allow-same-origin would let it drop the sandbox anyway. -->
    <iframe
      v-else-if="showIframe"
      title="Plugin Widget Iframe"
      :sandbox="iframeSandbox"
      :src="iframeUrl"
      :height="height"
      width="100%"
      @error="hasError = true"/>
    <div
      v-else
      class="cy-plugin-widget-iframe__placeholder"
      :style="{ height: `${height}px` }"/>
  </div>
</template>

<script>
import CyEmptyData from '@/components/CyEmptyData.vue'
import { useAuthStore } from '@/store/pinia/auth'

export default {
  name: 'CyPluginWidgetIframe',
  components: { CyEmptyData },
  props: {
    iframeContext: {
      type: Object,
      default: null,
    },
    height: {
      type: [String, Number],
      default: '100%',
    },
  },
  data: () => ({
    hasError: false,
    checked: false,
    errorDetail: null,
  }),
  computed: {
    showIframe () {
      return Boolean(this.iframeUrl) && this.checked
    },
    iframeUrl () {
      if (!this.iframeContext) return null

      const {
        scope,
        orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
        widgetId,
        path,
      } = this.iframeContext

      if (!orgCanonical || !widgetId) return null

      const baseUrl = import.meta.env.VUE_APP_API_URL
      let endpoint = ''

      const authStore = useAuthStore()

      if (scope === 'component' && projectCanonical && envCanonical && componentCanonical) {
        endpoint = `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugin_widgets/${widgetId}/${authStore.jwt}/iframe/`
      } else {
        endpoint = `/organizations/${orgCanonical}/plugin_widgets/${widgetId}/${authStore.jwt}/iframe/`
      }

      // Handle baseUrl being an absolute or relative path
      const url = new URL(endpoint, baseUrl.startsWith('http') ? baseUrl : window.location.origin + baseUrl)

      if (path) {
        url.searchParams.append('path', path)
      }

      const { routeSuffix, routeQuery, routeHash } = this.iframeContext

      if (routeSuffix) {
        url.pathname = url.pathname.replace(/\/$/, '') + '/' + routeSuffix
      }
      if (routeQuery) {
        for (const [k, v] of Object.entries(routeQuery)) {
          url.searchParams.append(k, v)
        }
      }
      if (routeHash) {
        url.hash = routeHash
      }

      return url.toString()
    },
    iframeSandbox () {
      if (!this.iframeUrl) return null

      const isSameOrigin = new URL(this.iframeUrl).origin === window.location.origin

      return isSameOrigin
        ? null
        : 'allow-scripts allow-forms allow-popups allow-same-origin allow-popups-to-escape-sandbox'
    },
  },
  watch: {
    iframeUrl: {
      immediate: true,
      handler (url) {
        this.hasError = false
        this.checked = false
        this.errorDetail = null
        if (url) this.checkIframe()
      },
    },
  },
  mounted () {
    window.addEventListener('message', this.onIframeMessage)
  },
  beforeUnmount () {
    window.removeEventListener('message', this.onIframeMessage)
  },
  methods: {
    onIframeMessage (event) {
      if (!this.iframeUrl) return
      const iframeOrigin = new URL(this.iframeUrl).origin
      if (event.origin !== iframeOrigin) return

      const { type, path, query, hash } = event.data || {}
      if (type !== 'cycloid:navigate') return

      this.$router.replace({
        name: this.$route.name,
        params: { ...this.$route.params, pathSuffix: path || '' },
        ...(query && Object.keys(query).length ? { query } : {}),
        ...(hash ? { hash } : {}),
      }).catch(() => {})
    },
    async checkIframe () {
      const {
        scope,
        orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
        widgetId,
      } = this.iframeContext

      const { jwt } = useAuthStore()
      const urlAtRequest = this.iframeUrl

      const response = (scope === 'component' && projectCanonical && envCanonical && componentCanonical)
        ? await this.$cycloid.ydAPI.getComponentPluginWidgetIframeProxy(orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId, jwt)
        : await this.$cycloid.ydAPI.getPluginWidgetIframeProxy(orgCanonical, widgetId, jwt)

      // Drop a stale preflight if the context changed while it was in flight.
      if (urlAtRequest !== this.iframeUrl) return

      if (!response?.ok) {
        try {
          const body = await response?.json()
          this.errorDetail = body?.errors?.[0]?.message || null
        } catch {
          this.errorDetail = null
        }
      }

      this.hasError = !response?.ok
      this.checked = true
    },
  },
}
</script>

<style lang="scss" scoped>
.cy-plugin-widget-iframe {
  width: 100%;
  height: 100%;

  iframe {
    border: none;
  }

  &__placeholder {
    width: 100%;
    background: cy-get-color('grey', 'light-2');
  }

  &__error-detail {
    margin-top: 8px;
    color: cy-get-color('grey', 'dark-1');
    font-size: 14px;
  }
}
</style>
